/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "timeago.js";
import { Search, Scale, ExternalLink } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import axiosInstance from "../../services/Axios/axios";

// Settlement queue for the support desk, mirroring the loans module's
// /loans/settlement list (GET /settlement?settlement_type=loan). The endpoint
// returns a plain array, so search/paging are client-side.
//
// Rows open /support/settlement-detailed — the same component the loans module
// mounts, rendered in the support shell — so an agent can process a request
// (quote, calculator, mark paid, PDF) without the loans role. The settlement
// endpoints are JwtAuthGuard-only, so no extra permission is needed.

const PAGE_SIZE = 12;

const STATUS_STYLES = {
  processed: "bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
  pending: "bg-orange-50 dark:bg-orange-900/25 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800",
};

function SettlementLookup() {
  const history = useHistory();

  const [settlements, setSettlements] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchSettlements = async (status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ settlement_type: "loan" });
      if (status) params.set("status", status);
      const res = await axiosInstance.get(`/settlement?${params.toString()}`);
      setSettlements(res.data || []);
    } catch (e) {
      setSettlements([]);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }

    // Avatars in the background — never blocks the list.
    axiosInstance
      .get("/users/members?isPaginated=false&page=1&size=10000")
      .then((res) => {
        const members = res.data?.docs || res.data || [];
        const map = {};
        members.forEach((m) => {
          if (m.staff_id && m.profile_image) map[m.staff_id] = m.profile_image;
        });
        setProfiles(map);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchSettlements(statusFilter);
    setPage(0);
  }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search) return settlements;
    const q = search.toLowerCase();
    return settlements.filter(
      (s) =>
        s.member_name?.toLowerCase().includes(q) ||
        s.staff_id?.toLowerCase().includes(q) ||
        s.phone?.toLowerCase().includes(q)
    );
  }, [settlements, search]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const StatusBadge = ({ status }) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${
        STATUS_STYLES[status] || STATUS_STYLES.pending
      }`}
    >
      {status || "pending"}
    </span>
  );

  return (
    <RenderSupportPage title="Settlements">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-5">
        Loan settlement requests. Open a pending one to calculate and issue the
        settlement quote. Search by name, staff ID or phone.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by name, staff ID or phone"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div className="flex gap-1.5">
          {[
            { key: "", label: "All" },
            { key: "pending", label: "Pending" },
            { key: "processed", label: "Processed" },
          ].map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                statusFilter === f.key
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!hasLoaded && settlements.length === 0 ? (
        <PageSkeleton cards={0} rows={8} cols={5} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  {["Member", "Staff ID", "Phone", "Status", "Requested", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Scale size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        No settlement requests found
                      </p>
                    </td>
                  </tr>
                ) : (
                  pageItems.map((s) => (
                    <tr key={s._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <img
                            className="w-8 h-8 rounded-full border border-slate-200 dark:border-gray-600 object-cover"
                            src={profiles[s.staff_id] || "/images/dev/avatar1.png"}
                            alt={s.member_name}
                            onError={(e) => { e.target.onerror = null; e.target.src = "/images/dev/avatar1.png"; }}
                          />
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                            {s.member_name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{s.staff_id || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{s.phone || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                        {s.created_at ? format(s.created_at) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() =>
                              history.push(`/support/settlement-detailed?id=${s._id}`)
                            }
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-colors whitespace-nowrap"
                          >
                            {s.status === "pending" ? "Process" : "View"}
                          </button>
                          {s.staff_id && (
                            <button
                              title="Open member profile"
                              onClick={() =>
                                history.push(`/support/member-detailed?staff_id=${s.staff_id}`)
                              }
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                            >
                              <ExternalLink size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-gray-700">
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Page {page + 1} of {pageCount} · {filtered.length} total
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </RenderSupportPage>
  );
}

export default SettlementLookup;
